import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import { useEffect } from "preact/hooks";
import { IcoCalendar, IcoTrash, IcoUsers } from "../../components/icons";
import { BrandedLayout, Button, ConfirmDialog } from "../../components/ui";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import type { TeamVacation, Vacation } from "../../server/rpc/vacations";
import { showError } from "../../stores/errors";
import type { MimicState } from "../../stores/mimic";
import type { BrandColor, User } from "../../types";
import { isAdmin } from "../../types";
import { getErrorMessage } from "../../utils/errors";

type TimeOffPageState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  isOrgAdmin: boolean;
  hasReports: boolean;
  vacations: Vacation[];
  teamVacations: TeamVacation[];
  isManager: boolean;
};

export async function load(args: LoaderArgs): Promise<TimeOffPageState> {
  const orgId = args.params.orgId ?? "";

  // Auth first — prevents raw 401 from data endpoints racing ahead of redirect
  const authResult = await rpc.auth.me({});
  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  const currentUser = authResult.user as User;

  const [orgsResult, orgResult, { vacations }, teamVacationsResult] =
    await Promise.all([
      rpc.orgs.list({}),
      rpc.orgs.get({ id: orgId }),
      rpc.vacations.list({ workspaceId: orgId }),
      rpc.vacations.listForTeam({ workspaceId: orgId }),
    ]);

  const currentMember = orgResult.currentMember;
  const isOrgAdmin = isAdmin(currentMember.role);

  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  const hasReports = orgFromList?.hasReports ?? false;
  const isManager = hasReports || isOrgAdmin;

  return {
    currentUser,
    mimic: authResult.mimic ?? null,
    orgId,
    orgName: orgResult.organization.name,
    brandColor,
    isOrgAdmin,
    hasReports,
    vacations,
    teamVacations: teamVacationsResult.vacations,
    isManager,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Time Off");
  const {
    currentUser,
    mimic,
    orgId,
    orgName,
    brandColor,
    isOrgAdmin,
    hasReports,
    isManager,
  } = state.value;

  const teamVacations = state.value.teamVacations;

  const vacations = useSignal<Vacation[]>(state.value.vacations);
  useEffect(() => {
    vacations.value = state.value.vacations;
  }, [state.value.vacations]);

  // Form state
  const startDate = useSignal("");
  const endDate = useSignal("");
  const note = useSignal("");
  const isSaving = useSignal(false);

  // Delete confirmation
  const deletingId = useSignal<string | null>(null);
  const isDeleting = useSignal(false);

  async function handleAdd() {
    if (!startDate.value || !endDate.value) return;

    if (endDate.value < startDate.value) {
      showError("End date must be on or after start date");
      return;
    }

    isSaving.value = true;
    try {
      const { vacation } = await rpc.vacations.create({
        workspaceId: orgId,
        startDate: startDate.value,
        endDate: endDate.value,
        note: note.value.trim() || undefined,
      });

      // Insert sorted by start date
      const updated = [...vacations.value, vacation].sort((a, b) =>
        a.startDate.localeCompare(b.startDate),
      );
      vacations.value = updated;

      // Reset form
      startDate.value = "";
      endDate.value = "";
      note.value = "";
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isSaving.value = false;
    }
  }

  async function handleDelete() {
    if (!deletingId.value) return;
    isDeleting.value = true;
    try {
      await rpc.vacations.remove({
        workspaceId: orgId,
        vacationId: deletingId.value,
      });
      vacations.value = vacations.value.filter(
        (v) => v.id !== deletingId.value,
      );
      deletingId.value = null;
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isDeleting.value = false;
    }
  }

  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const now = today;

  // Split vacations into current/upcoming vs past
  const upcoming = vacations.value.filter((v) => v.endDate >= now);
  const past = vacations.value.filter((v) => v.endDate < now);

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={orgId}
      orgName={orgName}
      brandColor={brandColor}
      activeTab="home"
      isAdmin={isOrgAdmin}
      hasReports={hasReports}
    >
      <div class="max-w-2xl">
        <div class="mb-6">
          <h2 class="text-xl font-bold text-text mb-1">Time Off</h2>
          <p class="text-text-muted text-sm">
            Taking time off? Put check-ins on hold while you're away so you
            won't receive reminders or accumulate unsubmitted check-ins.
          </p>
        </div>

        {/* Add vacation form */}
        <div class="bg-surface-page rounded-lg border border-border p-5 mb-8">
          <h3 class="text-sm font-semibold text-text mb-4">
            Schedule time off
          </h3>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label
                class="block text-sm font-medium text-text-muted mb-1.5"
                for="start-date"
              >
                Start date
              </label>
              <input
                id="start-date"
                type="date"
                value={startDate.value}
                onInput={(e) => {
                  startDate.value = (e.target as HTMLInputElement).value;
                  // Auto-set end date if empty or before start
                  if (
                    !endDate.value ||
                    endDate.value < (e.target as HTMLInputElement).value
                  ) {
                    endDate.value = (e.target as HTMLInputElement).value;
                  }
                }}
                class="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text focus:outline-none focus:border-text-muted transition-colors text-sm"
              />
            </div>
            <div>
              <label
                class="block text-sm font-medium text-text-muted mb-1.5"
                for="end-date"
              >
                End date
              </label>
              <input
                id="end-date"
                type="date"
                value={endDate.value}
                min={startDate.value || today}
                onInput={(e) => {
                  endDate.value = (e.target as HTMLInputElement).value;
                }}
                class="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text focus:outline-none focus:border-text-muted transition-colors text-sm"
              />
            </div>
          </div>

          <div class="mb-4">
            <label
              class="block text-sm font-medium text-text-muted mb-1.5"
              for="note"
            >
              Note <span class="text-text-subtle font-normal">(optional)</span>
            </label>
            <input
              id="note"
              type="text"
              value={note.value}
              onInput={(e) => {
                note.value = (e.target as HTMLInputElement).value;
              }}
              placeholder="e.g., Family vacation, Conference"
              maxLength={500}
              class="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text placeholder-text-subtle focus:outline-none focus:border-text-muted transition-colors text-sm"
            />
          </div>

          <Button
            variant="primary"
            onClick={handleAdd}
            disabled={!startDate.value || !endDate.value || isSaving.value}
            loading={isSaving.value}
          >
            Schedule Time Off
          </Button>
        </div>

        {/* Upcoming / active vacations */}
        {upcoming.length > 0 && (
          <div class="mb-8">
            <h3 class="text-sm font-semibold text-text mb-3">
              Scheduled Time Off
            </h3>
            <div class="flex flex-col gap-2">
              {upcoming.map((v) => (
                <VacationRow
                  key={v.id}
                  vacation={v}
                  today={now}
                  onDelete={() => {
                    deletingId.value = v.id;
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Past vacations */}
        {past.length > 0 && (
          <div>
            <h3 class="text-sm font-semibold text-text-muted mb-3">
              Past Time Off
            </h3>
            <div class="flex flex-col gap-2">
              {past.map((v) => (
                <VacationRow
                  key={v.id}
                  vacation={v}
                  today={now}
                  onDelete={() => {
                    deletingId.value = v.id;
                  }}
                  isPast
                />
              ))}
            </div>
          </div>
        )}

        {upcoming.length === 0 && past.length === 0 && (
          <div class="text-center py-8 text-text-muted text-sm">
            <IcoCalendar class="size-8 mx-auto mb-2 text-text-subtle" />
            <p>No time off scheduled</p>
            <p class="text-text-subtle mt-1">
              Use the form above to schedule your next break.
            </p>
          </div>
        )}

        {/* Team Time Off (managers/admins only) */}
        {isManager && teamVacations.length > 0 && (
          <TeamTimeOff vacations={teamVacations} today={now} />
        )}
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deletingId.value !== null}
        title="Cancel Time Off"
        confirmLabel="Remove"
        onConfirm={handleDelete}
        onCancel={() => {
          deletingId.value = null;
        }}
      >
        Are you sure you want to remove this time off entry?
      </ConfirmDialog>
    </BrandedLayout>
  );
}

// ============================================================================
// VacationRow
// ============================================================================

function VacationRow({
  vacation,
  today,
  onDelete,
  isPast,
}: {
  vacation: Vacation;
  today: string;
  onDelete: () => void;
  isPast?: boolean;
}) {
  const isActive = vacation.startDate <= today && vacation.endDate >= today;

  return (
    <div
      class={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${
        isActive
          ? "border-[var(--brand-200)] bg-[var(--brand-50)]"
          : isPast
            ? "border-border-muted bg-surface-page opacity-60"
            : "border-border bg-surface"
      }`}
    >
      <div class="flex-shrink-0">
        <IcoCalendar
          class={`size-5 ${isActive ? "text-[var(--brand-600)]" : "text-text-muted"}`}
        />
      </div>

      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <span
            class={`text-sm font-medium ${isActive ? "text-[var(--brand-700)]" : "text-text"}`}
          >
            {formatVacationDate(vacation.startDate)} &ndash;{" "}
            {formatVacationDate(vacation.endDate)}
          </span>
          {isActive && (
            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--brand-100)] text-[var(--brand-700)]">
              Currently away
            </span>
          )}
          {!isActive && !isPast && (
            <span class="text-xs text-text-subtle">
              {daysUntil(vacation.startDate, today)}
            </span>
          )}
        </div>
        {vacation.note && (
          <p
            class={`text-sm mt-0.5 ${isActive ? "text-[var(--brand-600)]" : "text-text-muted"}`}
          >
            {vacation.note}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={onDelete}
        class="flex-shrink-0 p-1.5 rounded-md text-text-subtle hover:text-red-600 hover:bg-red-50 transition-colors"
        title="Remove time off"
      >
        <IcoTrash class="size-4" />
      </button>
    </div>
  );
}

function formatVacationDate(dateStr: string): string {
  const date = new Date(`${dateStr}T12:00:00`); // Avoid timezone issues
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year:
      date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
}

function daysUntil(dateStr: string, today: string): string {
  const target = new Date(`${dateStr}T00:00:00`);
  const now = new Date(`${today}T00:00:00`);
  const diff = Math.ceil(
    (target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (diff === 1) return "Starts tomorrow";
  if (diff <= 7) return `Starts in ${diff} days`;
  return `Starts ${formatVacationDate(dateStr)}`;
}

// ============================================================================
// Team Time Off (manager/admin view)
// ============================================================================

function TeamTimeOff({
  vacations,
  today,
}: {
  vacations: TeamVacation[];
  today: string;
}) {
  // Split into active and upcoming
  const active = vacations.filter(
    (v) => v.startDate <= today && v.endDate >= today,
  );
  const upcoming = vacations.filter((v) => v.startDate > today);

  return (
    <div class="mt-10 pt-8 border-t border-border">
      <div class="flex items-center gap-2 mb-4">
        <IcoUsers class="size-5 text-text-muted" />
        <h2 class="text-lg font-bold text-text">Team Time Off</h2>
      </div>

      {active.length > 0 && (
        <div class="mb-6">
          <h3 class="text-sm font-semibold text-sky-600 dark:text-sky-400 uppercase tracking-wide mb-3">
            Currently Away ({active.length})
          </h3>
          <div class="flex flex-col gap-2">
            {active.map((v) => (
              <TeamVacationRow key={v.id} vacation={v} today={today} />
            ))}
          </div>
        </div>
      )}

      {upcoming.length > 0 && (
        <div>
          <h3 class="text-sm font-semibold text-text-muted uppercase tracking-wide mb-3">
            Upcoming ({upcoming.length})
          </h3>
          <div class="flex flex-col gap-2">
            {upcoming.map((v) => (
              <TeamVacationRow key={v.id} vacation={v} today={today} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TeamVacationRow({
  vacation,
  today,
}: {
  vacation: TeamVacation;
  today: string;
}) {
  const isActive = vacation.startDate <= today && vacation.endDate >= today;
  const initial = vacation.memberName.charAt(0).toUpperCase();

  return (
    <div
      class={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
        isActive
          ? "border-sky-200 dark:border-sky-800/50 bg-sky-50/50 dark:bg-sky-950/20"
          : "border-border bg-surface"
      }`}
    >
      {/* Avatar */}
      <div
        class={`size-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 overflow-hidden ${
          isActive
            ? "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400"
            : "bg-surface-muted text-text-muted"
        }`}
      >
        {vacation.profilePictureUrl ? (
          <img
            src={vacation.profilePictureUrl}
            alt={vacation.memberName}
            class="size-full object-cover"
          />
        ) : (
          initial
        )}
      </div>

      {/* Name + dates */}
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <span
            class={`text-sm font-medium truncate ${isActive ? "text-sky-700 dark:text-sky-400" : "text-text"}`}
          >
            {vacation.memberName}
          </span>
          {isActive && (
            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 flex-shrink-0">
              Away
            </span>
          )}
        </div>
        <div class="flex items-center gap-1.5 text-xs text-text-muted">
          <span>
            {formatVacationDate(vacation.startDate)} &ndash;{" "}
            {formatVacationDate(vacation.endDate)}
          </span>
          {vacation.note && (
            <>
              <span class="text-border">·</span>
              <span class="truncate">{vacation.note}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
